import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Pagination,
    Container,
    Alert,
    Button
} from '@mui/material';
import { Person as PersonIcon, Home as HomeIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import UserCard from '../user/UserCard';
import axios from 'axios';
import RenterSearchFilter from './RenterSearchFilter';
import { RENTER_RENT_RANGE, AGE_RANGE } from './RenterSearchFilter';
import { useAuth } from '../../contexts/AuthContext';
import Seo from '../../components/common/Seo';
import { getRenterSearchResultSchema, getBreadcrumbSchema } from '../../utils/structuredDataHelpers';

function RenterSearch() {
    const { currentUser, blockedUsers } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    // URLからフィルター状態を初期化
    const initialFilters = {
        area: queryParams.get('area') || '',
        stayDuration: queryParams.get('stayDuration') || '',
        gender: queryParams.get('gender') || '',
        ageRange: [
            parseInt(queryParams.get('minAge')) || AGE_RANGE.min,
            parseInt(queryParams.get('maxAge')) || AGE_RANGE.max
        ],
        priceRange: [
            parseInt(queryParams.get('minPrice')) || RENTER_RENT_RANGE.min,
            parseInt(queryParams.get('maxPrice')) || RENTER_RENT_RANGE.max
        ],
        sortBy: queryParams.get('sortBy') || '',
        moveInPreference: queryParams.get('moveInPreference') || '',
        matchingPreferences: {
            smoking: queryParams.get('smoking') || '',
            pets: queryParams.get('pets') || '',
            lifeRhythm: queryParams.get('lifeRhythm') || '',
            cleanliness: queryParams.get('cleanliness') || '',
            communication: queryParams.get('communication') || '',
            stayingHome: queryParams.get('stayingHome') || '',
            visitors: queryParams.get('visitors') || ''
        }
    };

    const [searchResults, setSearchResults] = useState({ users: [], totalPages: 0, currentPage: 1, totalUsers: 0, realUsersCount: 0, sampleUsersCount: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(parseInt(queryParams.get('page')) || 1);
    const [filters, setFilters] = useState(initialFilters);

    // フィルター変更時にURLを更新
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.area) params.set('area', filters.area);
        if (filters.stayDuration) params.set('stayDuration', filters.stayDuration);
        if (filters.gender) params.set('gender', filters.gender);
        if (filters.ageRange[0] > AGE_RANGE.min) params.set('minAge', filters.ageRange[0]);
        if (filters.ageRange[1] < AGE_RANGE.max) params.set('maxAge', filters.ageRange[1]);

        if (filters.priceRange[0] > RENTER_RENT_RANGE.min) params.set('minPrice', filters.priceRange[0]);
        if (filters.priceRange[1] < RENTER_RENT_RANGE.max) params.set('maxPrice', filters.priceRange[1]);
        if (filters.sortBy) params.set('sortBy', filters.sortBy);
        if (filters.moveInPreference) params.set('moveInPreference', filters.moveInPreference);
        if (page > 1) params.set('page', page);

        // マッチング条件をURLに追加
        if (filters.matchingPreferences?.smoking) params.set('smoking', filters.matchingPreferences.smoking);
        if (filters.matchingPreferences?.pets) params.set('pets', filters.matchingPreferences.pets);
        if (filters.matchingPreferences?.lifeRhythm) params.set('lifeRhythm', filters.matchingPreferences.lifeRhythm);
        if (filters.matchingPreferences?.cleanliness) params.set('cleanliness', filters.matchingPreferences.cleanliness);
        if (filters.matchingPreferences?.communication) params.set('communication', filters.matchingPreferences.communication);
        if (filters.matchingPreferences?.stayingHome) params.set('stayingHome', filters.matchingPreferences.stayingHome);
        if (filters.matchingPreferences?.visitors) params.set('visitors', filters.matchingPreferences.visitors);

        navigate({ search: params.toString() }, { replace: true });
    }, [filters, page, navigate]);

    const filterBlockedUsers = useCallback((users) => {
        return users.filter(user => !blockedUsers.includes(user._id));
    }, [blockedUsers]);

    const handleSearch = useCallback(async () => {
        setIsLoading(true);
        try {
            // マッチング条件を個別のパラメータとして送信するため、フィルターをリストラクチャ
            const searchParams = {
                ...filters,
                page: page,
                // ネストしたオブジェクトをフラットにする
                ...filters.matchingPreferences
            };

            // ネストしたオブジェクトは削除（APIにはフラット構造で送る）
            delete searchParams.matchingPreferences;

            // area パラメータが存在する場合は keyword としても送信
            if (searchParams.area) {
                searchParams.keyword = searchParams.area;
            }

            const response = await axios.get('/api/users/renters/search', {
                params: searchParams
            });

            const filteredUsers = filterBlockedUsers(response.data.users);

            setSearchResults(prev => ({
                users: filteredUsers,
                totalPages: response.data.totalPages,
                currentPage: response.data.currentPage,
                totalUsers: response.data.totalUsers,
                realUsersCount: response.data.realUsersCount,
                sampleUsersCount: response.data.sampleUsersCount
            }));
        } catch (error) {
            console.error('検索エラー:', error);
        } finally {
            setIsLoading(false);
        }
    }, [filters, page, filterBlockedUsers]);

    useEffect(() => {
        handleSearch();
    }, [filters, page]);

    useEffect(() => {
        setSearchResults(prev => ({
            ...prev,
            users: filterBlockedUsers(prev.users)
        }));
    }, [blockedUsers, filterBlockedUsers]);

    const handleClearFilters = () => {
        setFilters({
            area: '',
            stayDuration: '',
            gender: '',
            ageRange: [AGE_RANGE.min, AGE_RANGE.max],
            priceRange: [RENTER_RENT_RANGE.min, RENTER_RENT_RANGE.max],
            sortBy: '',
            moveInPreference: '',
            matchingPreferences: {
                smoking: '',
                pets: '',
                lifeRhythm: '',
                cleanliness: '',
                communication: '',
                stayingHome: '',
                visitors: ''
            }
        });
        setPage(1);
    };

    // SEO設定を追加
    const getPageTitle = () => {
        if (!filters.area) {
            return 'ルームメイトを探す | ルームシェア相手募集';
        }

        return `${filters.area}でルームメイトを探す | ルームシェア相手募集`;
    };

    const getPageDescription = () => {
        if (!filters.area) {
            return 'ルームシェア相手を探せるマッチングサービス。理想のルームメイトを見つけて、新しい生活をスタートしましょう。';
        }

        return `${filters.area}エリアでルームメイトを募集中のユーザーを探せます。ライフスタイル、予算、入居時期などの条件であなたに合ったルームメイトを見つけましょう。`;
    };

    // カノニカルパス
    const getCanonicalPath = () => {
        if (!filters.area) {
            return '/renters';
        }

        return `/renters?area=${encodeURIComponent(filters.area)}`;
    };

    // 地域特化コンテンツを追加
    const renderLocationSpecificContent = () => {
        if (filters.area) {
            return (
                <Box mt={3} mb={4}>
                    <Typography variant="h5" gutterBottom>{filters.area}のルームメイト</Typography>
                    <Typography variant="body1">
                        {filters.area}エリアでルームシェア相手をお探しの方向けの情報です。
                        理想のルームメイトを見つけるために、エリア、年齢、ライフスタイルなどの条件で絞り込みができます。
                    </Typography>
                </Box>
            );
        }

        return null;
    };

    // パンくずリスト用データを追加
    const getBreadcrumbs = () => {
        const breadcrumbs = [
            { name: 'トップ', path: '/' },
            { name: 'ルームメイト検索', path: '/renters' }
        ];

        // エリアが指定されている場合は追加
        if (filters.area) {
            breadcrumbs.push({
                name: `${filters.area}のルームメイト`,
                path: `/renters?area=${encodeURIComponent(filters.area)}`
            });
        }

        return breadcrumbs;
    };

    // 構造化データの配列を生成
    const getSchemaArray = () => {
        // ルームメイト検索結果のスキーマ
        const searchSchema = getRenterSearchResultSchema(searchResults.users);
        // パンくずリストのスキーマ
        const breadcrumbSchema = getBreadcrumbSchema(getBreadcrumbs());

        return [searchSchema, breadcrumbSchema].filter(Boolean);
    };

    return (
        <>
            {/* SEOコンポーネントを追加 */}
            <Seo
                title={getPageTitle()}
                description={getPageDescription()}
                path={getCanonicalPath()}
                location={filters.area}
                schema={getSchemaArray()}
            />

            <Box
                sx={{
                    position: 'sticky',
                    top: { xs: '56px', sm: '64px' },
                    zIndex: 1100,
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease-in-out',
                    py: { xs: 0.5, sm: 1 },
                    mb: { xs: 2, sm: 1 },
                }}
            >
                <RenterSearchFilter
                    filters={filters}
                    setFilters={setFilters}
                    onClear={handleClearFilters}
                    totalUsers={searchResults.totalUsers}
                />
            </Box>

            <Container maxWidth="lg">
                {/* 地域特化コンテンツを追加 */}
                {renderLocationSpecificContent()}

                {/* 新規ユーザー向けの案内 */}
                {!currentUser && (
                    <Alert
                        severity="info"
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1), rgba(93, 173, 226, 0.05))',
                            border: '1px solid rgba(52, 152, 219, 0.2)',
                            borderRadius: '12px',
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                    <PersonIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1.1rem' }} />
                                    一緒に住む人を探しています
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    物件を一緒に探したり、相手と住み方を決めたい人の投稿です
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate('/room-search')}
                                    startIcon={<HomeIcon />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        borderColor: '#3498db',
                                        color: '#3498db',
                                        '&:hover': {
                                            backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                        }
                                    }}
                                >
                                    物件を探す
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => navigate('/register')}
                                    sx={{
                                        fontSize: '0.75rem',
                                        background: 'linear-gradient(135deg, #3498db, #5dade2)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #2980b9, #3498db)',
                                        }
                                    }}
                                >
                                    無料登録
                                </Button>
                            </Box>
                        </Box>
                    </Alert>
                )}

                <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                    {!isLoading && searchResults.users.length > 0 && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                            sx={{ mb: { xs: 1, sm: 0.5 }, fontSize: '0.875rem' }}
                        >
                            全{searchResults.totalUsers}件中
                            {(searchResults.currentPage - 1) * 12 + 1}～
                            {Math.min(searchResults.currentPage * 12, searchResults.totalUsers)}件を表示
                        </Typography>
                    )}

                    {isLoading ? (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    ) : searchResults.users.length > 0 ? (
                        <>
                            <Grid container spacing={2}>
                                {searchResults.users.map((user) => (
                                    <Grid item xs={12} sm={6} md={4} key={user._id}>
                                        <UserCard user={user} />
                                    </Grid>
                                ))}
                            </Grid>
                            <Box display="flex" justifyContent="center" mt={4}>
                                <Pagination
                                    count={searchResults.totalPages}
                                    page={searchResults.currentPage}
                                    onChange={(event, value) => {
                                        setPage(value);
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'smooth'
                                        });
                                    }}
                                    color="primary"
                                />
                            </Box>
                        </>
                    ) : (
                        <Box textAlign="center">
                            <Typography color="text.secondary">
                                {filters.area ? '検索条件に一致するルームメイトが見つかりませんでした' : 'ルームメイト情報を読み込んでいます...'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Container>
        </>
    );
}

export default RenterSearch; 